import { CalendarDays } from 'lucide-react';
import { useCalendar, type AgendaEvent } from '../../hooks/useWidgetsData';
import { useNow } from '../../hooks/useNow';
import { useSettings } from '../../store/settings';

function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function dayLabel(d: Date, today: Date): string {
  const t = new Date(today);
  if (dayKey(d) === dayKey(t)) return 'Today';
  t.setDate(t.getDate() + 1);
  if (dayKey(d) === dayKey(t)) return 'Tomorrow';
  return new Intl.DateTimeFormat(undefined, { weekday: 'long', day: 'numeric', month: 'short' }).format(d);
}

/** All-day events are dates, not instants: show them on their calendar day regardless of zone. */
function localStart(e: AgendaEvent): Date {
  if (!e.allDay) return new Date(e.start);
  const d = new Date(e.start);
  return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function Calendar({ maxEvents = 8 }: { maxEvents?: number }) {
  const sources = useSettings((s) => s.calendar.sources);
  const hour12 = useSettings((s) => s.clock.hour12);
  const now = useNow('minute');
  const { data, error, loading } = useCalendar();

  if (!sources.some((s) => s.enabled)) {
    return (
      <div className="glass muted p-4 text-sm">
        Add a calendar address (iCloud, Google or Outlook .ics link) in Settings → Calendar.
      </div>
    );
  }

  const upcoming = (data ?? []).filter((e) => new Date(e.end) > now).slice(0, maxEvents);
  const groups = new Map<string, { label: string; events: AgendaEvent[] }>();
  for (const e of upcoming) {
    const start = localStart(e);
    const key = dayKey(start < now ? now : start);
    const g = groups.get(key) ?? { label: dayLabel(start < now ? now : start, now), events: [] };
    g.events.push(e);
    groups.set(key, g);
  }
  const fmtTime = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit', hour12 });

  return (
    <div className="glass p-4">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium uppercase tracking-wider opacity-80">
        <CalendarDays className="h-4 w-4" aria-hidden /> Up next
      </div>
      {!data && <div className="muted text-sm">{loading ? 'Loading events…' : error}</div>}
      {data && upcoming.length === 0 && <div className="muted text-sm">Nothing scheduled. Enjoy the calm.</div>}
      <div className="space-y-3">
        {[...groups.values()].map((g) => (
          <div key={g.label}>
            <div className="muted mb-1 text-xs font-semibold uppercase tracking-wider">{g.label}</div>
            <ul className="space-y-1.5">
              {g.events.map((e) => (
                <li key={`${e.uid}-${e.start}`} className="flex items-start gap-3">
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: e.calendar.color }} />
                  <span className="muted w-14 shrink-0 text-sm tabular-nums">
                    {e.allDay ? 'All day' : fmtTime.format(new Date(e.start))}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{e.title}</span>
                    {e.location && <span className="muted block truncate text-xs">{e.location}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
